import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'All Products' },
  { id: 'apparel', label: 'Apparel' },
  { id: 'plushie', label: 'Plushies' },
  { id: 'supplement', label: 'Supplements' },
];

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const videoRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeCategory !== 'all' ? `?category=${activeCategory}` : '';
      const response = await axios.get(`${API}/products${params}`);
      setProducts(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    fetchProducts();
    // Auto-play video when page loads
    if (videoRef.current) {
      videoRef.current.play().catch(() => {/* Video autoplay blocked */});
    }
  }, [fetchProducts]);

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="shop-page">
      <div className="container-custom">
        {/* Opening Soon Banner with Video */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-8 rounded-2xl overflow-hidden"
          style={{ height: '20vh', minHeight: '150px' }}
        >
          {/* Video Background */}
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/q53nalct_openart-video_084ca944_1774833120710.mp4" type="video/mp4" />
          </video>
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/50" />
          {/* Text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <h2 
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-white text-center"
              style={{ 
                fontFamily: 'Playfair Display, serif',
                textShadow: '4px 4px 8px rgba(0,0,0,0.8), 0 0 40px rgba(244, 196, 48, 0.5)'
              }}
            >
              Opening Soon
            </h2>
          </div>
        </motion.div>

        {/* Shop Logo Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-12"
        >
          <div className="max-w-3xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/a85ql2rw_Firefly_Gemini%20Flash_Combine%20to%20create%20%20shop%20logo%20brand%20for%20market%20display%2C%20-%20The%20Grad%20Fawkes%20Gnostic%20empo%20171003.png"
              alt="The Grad Fawkes Gnostic Emporium - Bend, Oregon"
              className="w-full h-auto rounded-2xl shadow-2xl"
              style={{ boxShadow: '0 0 60px rgba(244, 196, 48, 0.3)' }}
            />
          </div>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="section-subheader max-w-2xl mx-auto">
            Official Grad P Fawkes merchandise. Apparel, plushies, and the revolutionary CocoaLife™ supplement.
          </p>
        </motion.div>

        {/* Music Store Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="max-w-4xl mx-auto grid gap-4 md:grid-cols-2">
            {/* DistroKid Link */}
            <a 
              href="https://direct.distrokid.com/gradfawkes/"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="distrokid-music-link"
            >
              <div 
                className="relative p-5 rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.02] h-full"
                style={{ 
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  boxShadow: '0 0 30px rgba(244, 196, 48, 0.2), 0 10px 40px rgba(0,0,0,0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-accent/10 via-transparent to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-accent to-secondary flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display' }}>
                      GradTrax Music
                    </h3>
                    <p className="text-gray-400 text-xs">
                      Stream & Download on all platforms
                    </p>
                  </div>
                  <div className="bg-accent text-black px-4 py-1.5 rounded-full font-bold text-xs group-hover:bg-white transition-colors whitespace-nowrap">
                    LISTEN →
                  </div>
                </div>
              </div>
            </a>

            {/* Spotify Album Link */}
            <a 
              href="https://open.spotify.com/album/5X28LU89J7lax1aZdgZJEX?si=wAO-MVphRHW_nCM8pIWicg"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="spotify-album-link"
            >
              <div 
                className="relative p-5 rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.02] h-full"
                style={{ 
                  background: 'linear-gradient(135deg, #191414 0%, #1DB954 100%)',
                  boxShadow: '0 0 30px rgba(29, 185, 84, 0.2), 0 10px 40px rgba(0,0,0,0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#1DB954]/20 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#1DB954] flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-black" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display' }}>
                      Latest Album
                    </h3>
                    <p className="text-gray-300 text-xs">
                      Listen on Spotify
                    </p>
                  </div>
                  <div className="bg-[#1DB954] text-black px-4 py-1.5 rounded-full font-bold text-xs group-hover:bg-white transition-colors whitespace-nowrap">
                    PLAY →
                  </div>
                </div>
              </div>
            </a>
          </div>
        </motion.div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-12">
          <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto p-0">
            {categories.map(category => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className={`category-pill ${
                  activeCategory === category.id ? 'category-pill-active' : 'category-pill-inactive'
                }`}
                data-testid={`shop-category-${category.id}`}
              >
                {category.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="space-y-4">
                <div className="aspect-square skeleton rounded-2xl" />
                <div className="h-4 skeleton w-3/4" />
                <div className="h-6 skeleton w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/product/${product.id}`}>
                  <Card
                    className="product-card group"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      
                      {/* Quick add button */}
                      <Button
                        size="icon"
                        className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all bg-secondary hover:bg-secondary/90 rounded-full w-12 h-12"
                        onClick={(e) => handleAddToCart(e, product)}
                        data-testid={`quick-add-${product.id}`}
                      >
                        <ShoppingCart className="w-5 h-5" />
                      </Button>

                      {/* Category badge */}
                      <span className="absolute top-3 left-3 badge-accent capitalize">
                        {product.category}
                      </span>

                      {/* Stock warning */}
                      {product.stock < 20 && (
                        <span className="absolute top-3 right-3 bg-destructive text-white px-2 py-1 rounded-full text-xs font-bold">
                          Low Stock
                        </span>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-bold text-lg mb-1 line-clamp-1">{product.name}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-secondary font-bold text-xl">${product.price.toFixed(2)}</p>
                        {product.details?.savings && (
                          <span className="text-accent text-sm font-semibold">
                            {product.details.savings}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}

        {products.length === 0 && !loading && (
          <div className="text-center py-20">
            <p className="text-muted-foreground">No products found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
}
